import React, { useState, useEffect } from 'react';
import { Link, useParams, useLocation, Outlet } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import api, { clientAPI, referralAPI, atPacketAPI, fileReviewAPI, clientServicesAPI } from '../services/api';
import { getEffectivePermissions, hasPermission } from '../utils/permissions';
import { formatDisplayDate } from '../utils/dateFormat';
import { ageInMonthsDisplay } from '../utils/ageInMonths';
import SideNavLayout from './SideNavLayout';
import './ClientLayout.css';

function ClientLayout() {
  const { id: clientId } = useParams();
  const location = useLocation();
  const { tenantPath } = useTenantFromUrl();
  const pathname = location.pathname || '';

  const [client, setClient] = useState(null);
  const [currentReferral, setCurrentReferral] = useState(null);
  const [showAtPacket, setShowAtPacket] = useState(false);
  const [atPacketCount, setAtPacketCount] = useState(null);
  const [showFileReview, setShowFileReview] = useState(false);
  const [fileReviewCount, setFileReviewCount] = useState(null);
  const [showClientServices, setShowClientServices] = useState(false);
  const [clientServicesCount, setClientServicesCount] = useState(null);
  const [showChecklist, setShowChecklist] = useState(false);
  /** Tenant flag from /whoami (on unless explicitly off). */
  const [showClientWorkTasks, setShowClientWorkTasks] = useState(true);
  /** Permission codes from /whoami — same source as API checks; avoids stale localStorage after role changes. */
  const [whoamiPermissions, setWhoamiPermissions] = useState(null);

  useEffect(() => {
    if (!clientId) return;
    clientAPI.getById(clientId).then((res) => setClient(res.data?.data ?? null)).catch(() => setClient(null));
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    referralAPI.getByClientId(clientId)
      .then((res) => {
        const referrals = res.data?.data ?? [];
        setCurrentReferral(referrals.length > 0 ? referrals[0] : null);
      })
      .catch(() => setCurrentReferral(null));
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    api.get('/whoami').then((res) => {
      const atPacket = res.data?.features?.at_packet === true;
      const checklist = res.data?.features?.checklist === true;
      const fileReview = res.data?.features?.file_review === true;
      const clientServices = res.data?.features?.client_services_provider_search === true;
      setShowAtPacket(!!atPacket);
      setShowChecklist(!!checklist);
      setShowFileReview(!!fileReview);
      setShowClientServices(!!clientServices);
      setShowClientWorkTasks(res.data?.features?.client_work_tasks !== false);
      setWhoamiPermissions(Array.isArray(res.data?.permissions) ? res.data.permissions : []);
      if (atPacket) {
        atPacketAPI
          .list({
            clientId,
            pageSize: 1,
            excludeStatusCodes: 'Approved,PartiallyApproved,Denied',
          })
          .then((listRes) => setAtPacketCount(listRes.data?.total ?? 0))
          .catch(() => setAtPacketCount(0));
      }
      if (fileReview) {
        fileReviewAPI
          .listReviews({ client_id: clientId })
          .then((listRes) => {
            const list = (listRes.data?.data ?? []).filter((r) => r.status !== 'Review_Complete');
            setFileReviewCount(list.length);
          })
          .catch(() => setFileReviewCount(0));
      }
      if (clientServices) {
        clientServicesAPI
          .listNeeds({ clientId, pageSize: 1, status: 'open' })
          .then((listRes) => {
            setClientServicesCount(listRes.data?.total ?? 0);
          })
          .catch(() => setClientServicesCount(0));
      }
    }).catch(() => {
      setShowAtPacket(false);
      setShowChecklist(false);
      setShowFileReview(false);
      setShowClientServices(false);
      setShowClientWorkTasks(false);
      setWhoamiPermissions(null);
    });
  }, [clientId]);

  const getClientPath = (segment = '') => tenantPath(`/client/${clientId}${segment ? `/${segment}` : ''}`);

  const isActive = (segment) => {
    const base = pathname.replace(/\/$/, '');
    const target = getClientPath(segment).replace(/\/$/, '');
    if (!segment) return base === target || base === tenantPath(`/client/${clientId}`);
    return base === target || base.startsWith(target + '/');
  };

  const permsForNav = whoamiPermissions ?? getEffectivePermissions();

  // Nav labels and order: Client Edit, Client Email, Client Folder, Client Needs, Client Tracking, Doc Gen, AT Packets, etc.
  const navItems = [
    { segment: '', label: 'Client Edit' },
    { segment: 'email', label: 'Client Email' },
    { segment: 'folder', label: 'Client Folder' },
    ...(showClientServices && (hasPermission('client_services.manage_needs') || hasPermission('client_services.search_providers')) ? [{ segment: 'client-services', label: `Client Needs${clientServicesCount != null ? ` (${clientServicesCount})` : ''}` }] : []),
    ...(currentReferral ? [{ segment: 'milestones', label: 'Client Tracking' }] : []),
    { segment: 'documents-all', label: 'Doc Gen' },
    ...(showAtPacket && hasPermission('at_packets.read') ? [{ segment: 'at-packets', label: `AT Packets${atPacketCount != null ? ` (${atPacketCount})` : ''}` }] : []),
    ...(showFileReview && hasPermission('file_reviews.view') ? [{ segment: 'file-reviews', label: `File Review${fileReviewCount != null ? ` (${fileReviewCount})` : ''}` }] : []),
    ...(currentReferral ? [
      ...(hasPermission('provider_team.read') ? [{ segment: 'team', label: 'Provider Team' }] : []),
      ...(showChecklist && hasPermission('checklists.view') ? [{ segment: 'checklists', label: 'Checklist' }] : []),
      ...(showClientWorkTasks && permsForNav.includes('client_work_tasks.read') ? [{ segment: 'work-tasks', label: 'My To-Dos' }] : []),
    ] : []),
  ];

  const formatDob = (d) => (d ? formatDisplayDate(d) : '');
  const navClientAgeDisplay = client ? ageInMonthsDisplay(client.date_of_birth) : null;

  const nav = (
    <>
      <div className="client-layout-nav-header side-nav-header">
        <h2 className="client-layout-nav-title side-nav-title">Client</h2>
      </div>
      <div className="client-layout-nav-body">
        {client && (
          <div className="client-layout-nav-client-info">
            <div className="client-layout-nav-client-name">
              {client.last_name}, {client.first_name}
            </div>
            {client.date_of_birth && (
              <div className="client-layout-nav-client-dob">
                DOB: {formatDob(client.date_of_birth)}
              </div>
            )}
            {navClientAgeDisplay != null && (
              <div className="client-layout-nav-client-age" aria-live="polite">
                Age: {navClientAgeDisplay}
              </div>
            )}
          </div>
        )}
        {navItems.map((item) => (
          <Link
            key={item.segment || 'edit'}
            to={getClientPath(item.segment)}
            className={`side-nav-link ${isActive(item.segment) ? 'active' : ''}`}
          >
            {item.label}
          </Link>
        ))}
      </div>
    </>
  );

  return (
    <SideNavLayout
      nav={nav}
      navClassName="client-layout-nav"
      isolateCollapse
      collapseRail
      collapseRailStorageKey="cfc-side-nav-collapse-rail-client"
      collapseRailExpandTitle="Show client links"
      collapseRailCollapseTitle="Hide client links"
      collapseRailExpandAria="Expand client navigation panel"
      collapseRailCollapseAria="Collapse client navigation panel"
    >
      <Outlet context={{ clientId, currentReferral, client }} />
    </SideNavLayout>
  );
}

export default ClientLayout;
